import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getGrouponById,
  getMerchantReviews,
  type GrouponDeal,
  type PublicReview,
} from '@/lib/api';
import { formatPrice } from '@/lib/format';
import { pickTitle, pickName, pickDescription } from '@/lib/i18n-pick';
import { useLocale, useTk } from '@/lib/i18n';
import { useAuth } from '@/lib/use-auth';
import { trackBehavior } from '@/lib/track';
import { Button } from '@sabay/ui-kit';

interface LoadState {
  status: 'idle' | 'loading' | 'success' | 'error' | 'not-found';
  deal: GrouponDeal | null;
  errorMessage: string | null;
}

function normalizeImages(images: unknown): string[] {
  if (!Array.isArray(images)) return typeof images === 'string' && images ? [images] : [];
  return images.filter((item): item is string => typeof item === 'string' && item.length > 0);
}

export function GrouponDetailPage(): React.ReactElement {
  const tk = useTk();
  const locale = useLocale();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const viewTrackedRef = useRef<string | null>(null);
  const [state, setState] = useState<LoadState>({
    status: 'loading',
    deal: null,
    errorMessage: null,
  });

  useEffect(() => {
    if (state.status !== 'success' || !state.deal?.id) return;
    if (auth.status !== 'authenticated' || !auth.token) return;
    const dealId = state.deal.id;
    if (viewTrackedRef.current === dealId) return;
    viewTrackedRef.current = dealId;
    void trackBehavior('VIEW_GROUPON_DEAL', { grouponId: dealId }, auth.token);
  }, [state.status, state.deal?.id, auth.status, auth.token]);

  useEffect(() => {
    if (!id) return;
    const ac = new AbortController();
    setState({ status: 'loading', deal: null, errorMessage: null });
    getGrouponById(id, ac.signal)
      .then((deal) => setState({ status: 'success', deal, errorMessage: null }))
      .catch((err: Error & { code?: string }) => {
        if (ac.signal.aborted) return;
        if (err.code === 'GROUPON_NOT_FOUND') {
          setState({ status: 'not-found', deal: null, errorMessage: null });
        } else {
          setState({ status: 'error', deal: null, errorMessage: err.message });
        }
      });
    return () => ac.abort();
  }, [id]);

  const canBuy = auth.status === 'authenticated';
  const deal = state.deal;
  const galleryImages = deal ? normalizeImages(deal.images) : [];
  const coverImage = deal?.coverImageUrl ?? galleryImages[0] ?? deal?.merchant.coverImageUrl ?? null;
  const galleryOnly = galleryImages.filter((imageUrl) => imageUrl !== coverImage);
  const description = deal ? pickDescription(deal.descriptionI18n, locale) : null;
  const usageRules = deal ? pickDescription(deal.usageRulesI18n, locale) : null;
  const refundRules = deal ? pickDescription(deal.refundRulesI18n, locale) : null;

  return (
    <main className="min-h-screen px-6 py-8 space-y-4 pb-24">
      <header className="flex items-center justify-between">
        <Link to="/groupons" className="text-[var(--tg-link)] text-sm">
          ← {tk('nav.groupon')}
        </Link>
        <span />
        <span />
      </header>

      {state.status === 'loading' ? (
        <div className="rounded-lg border border-dashed border-neutral-300 p-10 text-center text-neutral-500">
          {tk('common.loading')}
        </div>
      ) : null}

      {state.status === 'not-found' ? (
        <div className="rounded-lg border border-dashed border-neutral-300 p-10 text-center text-neutral-500">
          {tk('groupon.detail.not_found')}
        </div>
      ) : null}

      {state.status === 'error' ? (
        <div className="rounded-lg border border-danger-500 bg-danger-50 p-4 text-sm text-danger-700">
          <p className="font-medium">{tk('common.error.generic')}</p>
          <p className="text-xs opacity-75 mt-1 break-all">{state.errorMessage}</p>
        </div>
      ) : null}

      {state.status === 'success' && state.deal ? (
        <>
          {coverImage ? (
            <section className="space-y-3">
            <img
              src={coverImage}
              alt={`${state.deal.titleZh} ${tk('groupon.detail.coverImage')}`}
              className="h-56 w-full rounded-2xl object-cover shadow-sm"
            />
              {galleryOnly.length > 0 ? (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {galleryOnly.map((imageUrl, index) => (
                    <img
                      key={imageUrl}
                      src={imageUrl}
                      alt={`${state.deal!.titleZh} ${tk('groupon.detail.image')} ${index + 1}`}
                      className="h-20 w-28 flex-none rounded-xl object-cover"
                    />
                  ))}
                </div>
              ) : null}
            </section>
          ) : null}

            <section className="space-y-2">
              <h1 className="text-2xl font-semibold">{pickTitle(state.deal, locale)}</h1>
              <p className="text-sm text-[var(--tg-hint)]">{pickName(state.deal.merchant, locale)}</p>
            </section>

          <section className="rounded-lg border border-neutral-200 bg-white p-4 space-y-2">
            <div className="flex items-baseline gap-2">
              <span className="text-brand-500 text-2xl font-semibold">
                {formatPrice(state.deal.priceCents, state.deal.currency)}
              </span>
              <span className="text-sm text-neutral-400 line-through">
                {formatPrice(state.deal.originalPriceCents, state.deal.currency)}
              </span>
            </div>
            {description ? (
              <p className="text-sm text-neutral-700 whitespace-pre-wrap">{description}</p>
            ) : null}
          </section>

          <InfoSection title={tk('groupon.detail.usage_rules')} body={usageRules} />
          <InfoSection title={tk('groupon.detail.refund_rules')} body={refundRules} />
          <GrouponReviewsSection merchantId={state.deal.merchantId} />

          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 px-6 py-3">
            <Button
              type="button"
              variant="primary"
              fullWidth
              disabled={!canBuy}
              onClick={() => navigate(`/checkout?dealId=${encodeURIComponent(state.deal!.id)}`)}
              className="py-3"
            >
              {canBuy ? tk('groupon.detail.buy_now') : tk('groupon.detail.open_in_telegram')}
            </Button>
          </div>
        </>
      ) : null}
    </main>
  );
}

function InfoSection({
  title,
  body,
}: {
  title: string;
  body: string | null;
}): React.ReactElement | null {
  if (!body) return null;
  return (
    <section className="rounded-lg border border-neutral-200 bg-white p-4 space-y-2">
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="text-sm text-neutral-700 whitespace-pre-wrap">{body}</p>
    </section>
  );
}

function GrouponReviewsSection({ merchantId }: { merchantId: string }): React.ReactElement {
  const tk = useTk();
  const [reviews, setReviews] = useState<{
    items: PublicReview[];
    total: number;
    averageRating: number | null;
  } | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    getMerchantReviews(merchantId, { pageSize: 5 }, ac.signal)
      .then((res) =>
        setReviews({
          items: res.items,
          total: res.total,
          averageRating: res.averageRating,
        }),
      )
      .catch(() => setReviews({ items: [], total: 0, averageRating: null }));
    return () => ac.abort();
  }, [merchantId]);

  if (!reviews) {
    return <div className="text-xs text-neutral-400 text-center">…</div>;
  }

  return (
    <section className="space-y-3">
      <div className="flex items-baseline gap-2">
        <h2 className="text-base font-semibold">
          {tk('groupon.detail.reviews')}（{reviews.total}）
        </h2>
        {reviews.averageRating !== null ? (
          <span className="text-sm text-[var(--tg-hint)]">
            ★ {reviews.averageRating.toFixed(1)}
          </span>
        ) : null}
      </div>
      {reviews.items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 p-4 text-center text-neutral-500 text-sm">
          {tk('groupon.detail.reviews_empty')}
        </div>
      ) : (
        <ul className="space-y-3">
          {reviews.items.map((review) => (
            <li
              key={review.id}
              className="rounded-lg border border-neutral-200 bg-white p-4 text-sm"
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-medium">
                  {review.reviewerName ?? tk('merchant.reviews.anonymous')}
                </span>
                <span className="text-warning-500">
                  {'★'.repeat(review.rating)}
                  {'☆'.repeat(5 - review.rating)}
                </span>
              </div>
              <p className="text-neutral-700 mt-1 whitespace-pre-wrap break-words">
                {review.comment}
              </p>
              {review.publishedAt ? (
                <p className="text-xs text-[var(--tg-hint)] mt-1">
                  {new Date(review.publishedAt).toLocaleDateString()}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
